/**
 * 陈述意见异步任务管理 API
 *
 * 该接口提供了陈述意见生成的异步任务管理功能，包括：
 * - 提交任务
 * - 查询任务状态
 * - 获取任务结果
 * - 取消任务
 *
 * @generated 从 OarController.java 生成
 * @date 2025-11-08
 */

/**
 * 权利要求分析结果
 */
export interface ClaimAnalysisResult {
    /** 涉及的权利要求（如 '1', '2-3', '5-6'） */
    claim_involved?: string;

    /** 审查员的对比分析 */
    examiner_comparison?: string;

    /** 核实结论 */
    verification_conclusion?: string;

    /** 对比文件公开/常识/未公开的内容分析 */
    disclosed_common_knowledge_not_disclosed?: string;

    /** 论证强度评估（强/中/弱） */
    argument_strength?: string;

    /** 推荐的应对行动 */
    recommended_action?: string;

    /** 策略建议 */
    strategy_recommendation?: string;

    /** 论证特征（逗号分隔） */
    argument_features?: string;
}

/**
 * 陈述意见生成响应
 */
export interface StatementOpinionResponse {
    /** 生成的陈述意见内容 */
    opinion_content: string;

    /** 任务ID */
    task_id?: string;

    /** 生成时间戳（毫秒） */
    generated_at?: number;

    /** 内容字符数 */
    word_count?: number;
}

// ==================== 提交任务 API ====================

/**
 * 提交陈述意见生成任务的请求体
 */
export interface SubmitTaskRequestBody {
    /** OAR 任务ID */
    task_id: string;

    /** 权利要求分析结果列表（由客户端调用权利要求分析服务后传入） */
    analyze_opinions_result?: ClaimAnalysisResult[];
}

/**
 * 提交任务的请求参数
 */
export interface SubmitTaskRequestParams {
    body: SubmitTaskRequestBody;
}

/**
 * 提交任务的响应数据
 */
export interface SubmitTaskResponseData {
    /** 异步任务ID（UUID） */
    async_task_id: string;

    /** 任务状态 */
    status: string;

    /** 响应消息 */
    message?: string;

    /** 任务创建时间戳（毫秒） */
    created_at?: number;
}

// ==================== 查询任务状态 API ====================

/**
 * 查询任务状态的路径参数
 */
export interface GetTaskStatusPathParams {
    /** 异步任务ID */
    async_task_id: string;
}

/**
 * 查询任务状态的请求参数
 */
export interface GetTaskStatusRequestParams {
    path: GetTaskStatusPathParams;
}

/**
 * 任务状态响应数据
 */
export interface TaskStatusResponseData {
    /** 异步任务ID */
    async_task_id: string;

    /**
   * 任务状态
   * 可选值: PENDING, PROCESSING, COMPLETED, FAILED, CANCELLED
   */
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

    /** 状态描述 */
    status_description?: string;

    /** 进度消息 */
    progress_message?: string;

    /** 任务创建时间戳（毫秒） */
    created_at?: number;

    /** 任务开始时间戳（毫秒） */
    started_at?: number;

    /** 任务完成时间戳（毫秒） */
    completed_at?: number;

    /** 执行时长（毫秒） */
    duration_ms?: number;

    /** 错误信息（任务失败时） */
    error_message?: string;

    /** 过期时间戳（毫秒） */
    expires_at?: number;

    /** 任务结果（仅当状态为 COMPLETED 时返回） */
    result?: StatementOpinionResponse;
}

// ==================== 获取任务结果 API ====================

/**
 * 获取任务结果的路径参数
 */
export interface GetTaskResultPathParams {
    /** 异步任务ID */
    async_task_id: string;
}

/**
 * 获取任务结果的请求参数
 */
export interface GetTaskResultRequestParams {
    path: GetTaskResultPathParams;
}

/**
 * 任务结果响应数据（与状态响应相同）
 */
export type TaskResultResponseData = TaskStatusResponseData;

// ==================== 取消任务 API ====================

/**
 * 取消任务的路径参数
 */
export interface CancelTaskPathParams {
    /** 异步任务ID */
    async_task_id: string;
}

/**
 * 取消任务的请求参数
 */
export interface CancelTaskRequestParams {
    path: CancelTaskPathParams;
}

/**
 * 取消任务的响应数据
 */
export type CancelTaskResponseData = boolean;

// ==================== API 接口定义 ====================

/**
 * 陈述意见异步任务管理 API 接口
 *
 * 提供完整的异步任务生命周期管理功能
 */
export interface StatementOpinionAsyncApi {
    /**
   * 生成陈述意见内容（异步）
   *
   * 异步调用 AI 服务生成陈述意见内容。
   * 此接口会立即返回任务ID，客户端需要通过任务ID轮询查询任务状态和结果。
   *
   * @param params - 请求参数，包含任务ID和权利要求分析结果
   * @returns Promise，返回异步任务提交响应
   */
    generateStatementOpinionAsync(
        params: SubmitTaskRequestParams
    ): Promise<SubmitTaskResponseData>;

    /**
   * 查询陈述意见生成任务状态
   *
   * 根据异步任务ID查询任务的当前状态和进度信息
   *
   * @param params - 请求参数，包含异步任务ID
   * @returns Promise，返回任务状态信息
   */
    getStatementOpinionTaskStatus(
        params: GetTaskStatusRequestParams
    ): Promise<TaskStatusResponseData>;

    /**
   * 获取陈述意见生成结果
   *
   * 获取已完成任务的生成结果。
   * 只有状态为 COMPLETED 的任务才会包含结果数据
   *
   * @param params - 请求参数，包含异步任务ID
   * @returns Promise，返回任务结果
   */
    getStatementOpinionTaskResult(
        params: GetTaskResultRequestParams
    ): Promise<TaskResultResponseData>;

    /**
   * 取消陈述意见生成任务
   *
   * 取消正在执行或等待执行的异步任务。
   * 只有 PENDING 或 PROCESSING 状态的任务可以被取消
   *
   * @param params - 请求参数，包含异步任务ID
   * @returns Promise，返回取消是否成功
   */
    cancelStatementOpinionTask(
        params: CancelTaskRequestParams
    ): Promise<CancelTaskResponseData>;
}

/**
 * 依赖注入的 Symbol
 */
export const StatementOpinionAsyncApi = Symbol.for('StatementOpinionAsyncApi');
